import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Decimal from 'decimal.js';
import debounce from 'lodash/debounce';
import { getIsAuthenticated, getPointsBalance } from 'store/user/userSelectors';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { MIN_POINTS_AMOUNT } from 'config';
import { useDataLayer } from 'hooks/useDataLayer';

export const DEFAULT_POINTS_AMOUNT = 20000;
const TRACKING_DEBOUCE_MILLIS = 300;

const emitInteraction = debounce((emitInteractionEvent) => {
  emitInteractionEvent({ type: 'Points and Pay Slider', value: `Slider Selected` });
}, TRACKING_DEBOUCE_MILLIS);

const getMaxCashAmount = ({ convertPointsToCash, totalCashAmount }) => {
  return new Decimal(totalCashAmount).minus(convertPointsToCash({ points: MIN_POINTS_AMOUNT }));
};

const getMinCashAmount = ({
  isAuthenticated,
  pointsBalanceInCash,
  totalCashAmount,
  payableAtPropertyCashAmount,
  payableAtBookingCashAmount,
}) => {
  if (!isAuthenticated) return payableAtPropertyCashAmount;

  if (pointsBalanceInCash.greaterThanOrEqualTo(payableAtBookingCashAmount)) {
    return payableAtPropertyCashAmount;
  } else {
    return totalCashAmount.minus(pointsBalanceInCash);
  }
};

const getInitialPointsAmount = ({ convertCashToPoints, totalCashAmount, minCashAmount }) => {
  const maxPoints = convertCashToPoints({ cash: totalCashAmount.minus(minCashAmount) });
  return Decimal.min(maxPoints, new Decimal(DEFAULT_POINTS_AMOUNT));
};

const getInitialCashAmount = ({ convertPointsToCash, totalCashAmount, initialPointsAmount }) => {
  const initialPointsAmountInCash = convertPointsToCash({ points: initialPointsAmount });
  return Decimal.max(totalCashAmount.minus(initialPointsAmountInCash), new Decimal(0));
};

const usePointsPaySlider = ({ totalCash, payableAtProperty, initialPointsAmount }) => {
  const { convertCashToPoints, convertPointsToCash } = usePointsConverters();

  if (totalCash.currency === 'PTS') throw new Error('Total must be supplied to points pay slider in cash');

  const isAuthenticated = useSelector(getIsAuthenticated);
  const pointsBalance = useSelector(getPointsBalance);
  const { emitInteractionEvent } = useDataLayer();

  const pointsBalanceInCash = pointsBalance ? convertPointsToCash({ points: pointsBalance }) : new Decimal(0);
  const totalCashAmount = new Decimal(totalCash.amount);
  const payableAtPropertyCashAmount = payableAtProperty ? new Decimal(payableAtProperty.amount) : new Decimal(0);
  const payableAtBookingCashAmount = totalCashAmount.minus(payableAtPropertyCashAmount);
  const maxCashAmount = getMaxCashAmount({ convertPointsToCash, totalCashAmount });

  const minCashAmount = getMinCashAmount({
    convertPointsToCash,
    convertCashToPoints,
    isAuthenticated,
    pointsBalanceInCash,
    totalCashAmount,
    payableAtPropertyCashAmount,
    payableAtBookingCashAmount,
    maxCashAmount,
  });

  initialPointsAmount = initialPointsAmount
    ? new Decimal(initialPointsAmount)
    : getInitialPointsAmount({
        convertPointsToCash,
        convertCashToPoints,
        totalCashAmount,
        minCashAmount,
      });

  const initialCashAmount = getInitialCashAmount({ convertPointsToCash, totalCashAmount, initialPointsAmount });

  const [pointsAmount, setPointsAmount] = useState(initialPointsAmount);
  const [cashAmount, setCashAmount] = useState(initialCashAmount);

  const pointsAmountInCash = totalCashAmount.minus(cashAmount);

  const onChange = useCallback(
    (value) => {
      setPointsAmount(convertCashToPoints({ cash: totalCashAmount.minus(value) }));
      setCashAmount(new Decimal(value));
      emitInteraction(emitInteractionEvent);
    },
    [convertCashToPoints, emitInteractionEvent, totalCashAmount],
  );

  return {
    pointsAmount,
    pointsAmountInCash,
    cashAmount,
    minCashAmount,
    maxCashAmount,
    initialCashAmount,
    onChange,
  };
};

export default usePointsPaySlider;
